import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  API_IP_ALLOWLIST_MAX,
  apiKeyIpMode,
  apiKeyIpModeLabel,
  type ApiKeyIpMode,
} from '../../../constants/enum';
import {
  gray40,
  gray80,
  primaryButtonBackgroundColor,
  red20,
  red50,
  tertiaryButtonBackgroundColor,
} from '../../../constants/colors';
import { dialogText, dialogTitle, regularXs } from '../../../constants/location';
import CustomTextfield from '../../uiParts/form/CustomTextfield';

interface Props {
  open: boolean;
  initialAllowlist: string[];
  /** IP 制限モード（#2982）: inherit=画面の許可リストを継承 / custom=専用リスト / none=制限なし */
  initialMode: ApiKeyIpMode;
  onClose: () => void;
  onSave: (ipList: string[], ipMode: ApiKeyIpMode) => Promise<void>;
}

// モードの表示順と補足説明
const MODE_OPTIONS: Array<{ value: ApiKeyIpMode; description: string }> = [
  {
    value: apiKeyIpMode.inherit,
    description: '設定 > セキュリティ > IP制限 の許可リストに従います。',
  },
  {
    value: apiKeyIpMode.custom,
    description: 'このキー専用の許可リストで判定します（画面用とは別系統）。',
  },
  {
    value: apiKeyIpMode.none,
    description: 'このキーは送信元 IP を制限しません。',
  },
];

/**
 * CIDR 形式かどうかの簡易チェック（IPv4 のみ。バックエンドで再検証）
 */
const isValidCidr = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed) return false;
  const match = trimmed.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})(?:\/(\d{1,2}))?$/);
  if (!match) return false;
  const [, a, b, c, d, mask] = match;
  if ([a, b, c, d].some(part => Number(part) > 255)) return false;
  if (mask !== undefined && (Number(mask) < 0 || Number(mask) > 32)) return false;
  return true;
};

const styles = {
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      width: '480px',
      '@media screen and (max-width:960px)': {
        padding: '32px 16px',
      },
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  dialogTitle,
  helperText: {
    ...dialogText,
    marginBottom: '16px',
  },
  metaRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '8px',
  },
  metaText: {
    ...regularXs,
    color: gray80,
  },
  metaErrorText: {
    ...regularXs,
    color: red50,
  },
  errorBox: {
    backgroundColor: red20,
    border: `1px solid ${red50}`,
    borderRadius: '4px',
    color: red50,
    fontSize: '14px',
    padding: '8px 12px',
    marginTop: '8px',
  },
  modeOption: {
    marginBottom: '8px',
  },
  modeDescription: {
    ...regularXs,
    color: gray80,
    marginLeft: '28px',
    marginTop: '-4px',
  },
  dialogActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '32px',
  },
  actionButton: {
    height: '44px',
    minWidth: '120px',
  },
};

const ApiKeyIpWhitelistEditor: React.FC<Props> = ({
  open,
  initialAllowlist,
  initialMode,
  onClose,
  onSave,
}) => {
  const [text, setText] = useState(initialAllowlist.join('\n'));
  const [mode, setMode] = useState<ApiKeyIpMode>(initialMode);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const { ipList, invalidLines } = useMemo(() => {
    const lines = text
      .split('\n')
      .map(l => l.trim())
      .filter(l => l.length > 0);
    const invalid = lines.filter(l => !isValidCidr(l));
    return { ipList: lines, invalidLines: invalid };
  }, [text]);

  const isCustom = mode === apiKeyIpMode.custom;

  const handleSubmit = async (): Promise<void> => {
    if (isCustom) {
      if (invalidLines.length > 0) {
        setErrorMessage(`不正なCIDR形式が含まれています: ${invalidLines.join(', ')}`);
        return;
      }
      if (ipList.length === 0) {
        setErrorMessage('専用の許可リストを指定する場合は 1 件以上入力してください。');
        return;
      }
      if (ipList.length > API_IP_ALLOWLIST_MAX) {
        setErrorMessage(`IPアドレスは ${API_IP_ALLOWLIST_MAX} 件までです。`);
        return;
      }
    }
    setErrorMessage('');
    setSubmitting(true);
    try {
      await onSave(isCustom ? ipList : [], mode);
      onClose();
    } catch (e) {
      const message = e instanceof Error ? e.message : '保存に失敗しました。';
      setErrorMessage(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} sx={styles.dialog}>
      <Box sx={styles.dialogHeader}>
        <Typography sx={styles.dialogTitle}>IP制限編集</Typography>
        <IconButton onClick={onClose} disabled={submitting} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Typography sx={styles.helperText}>
        このAPIキーの送信元 IP 制限モードを選択してください。
      </Typography>
      <RadioGroup value={mode} onChange={(e): void => setMode(e.target.value as ApiKeyIpMode)}>
        {MODE_OPTIONS.map(option => (
          <Box key={option.value} sx={styles.modeOption}>
            <FormControlLabel
              value={option.value}
              control={<Radio size="small" disabled={submitting} />}
              label={apiKeyIpModeLabel[option.value]}
            />
            <Typography sx={styles.modeDescription}>{option.description}</Typography>
          </Box>
        ))}
      </RadioGroup>
      {isCustom && (
        <>
          <Typography sx={styles.helperText}>
            1 行に 1 件、CIDR 形式（例: 203.0.113.0/24）で入力してください。
          </Typography>
          <CustomTextfield
            multiline
            minRows={6}
            maxRows={14}
            inputWidth="100%"
            fontSizeIs14px
            value={text}
            onChange={(e): void => setText(e.target.value)}
            placeholder="203.0.113.0/24"
            formMb="0"
            error={invalidLines.length > 0}
          />
          <Box sx={styles.metaRow}>
            <Typography sx={styles.metaText}>
              {ipList.length} 件 / 最大 {API_IP_ALLOWLIST_MAX} 件
            </Typography>
            {invalidLines.length > 0 && (
              <Typography sx={styles.metaErrorText}>
                不正な形式: {invalidLines.length} 件
              </Typography>
            )}
          </Box>
        </>
      )}
      {errorMessage && <Box sx={styles.errorBox}>{errorMessage}</Box>}

      <Box sx={styles.dialogActions}>
        <Button
          onClick={onClose}
          disabled={submitting}
          sx={[tertiaryButtonBackgroundColor, styles.actionButton]}
        >
          キャンセル
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting || (isCustom && invalidLines.length > 0)}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={[primaryButtonBackgroundColor, styles.actionButton]}
        >
          保存
        </Button>
      </Box>
    </Dialog>
  );
};

export default ApiKeyIpWhitelistEditor;
